import { useState, useEffect } from 'react';
import { Settings } from 'lucide-react';
import Modal from '../common/Modal';
import type { MtgeTenantAssignment, UpdateTenantAssignmentData } from '../../services/mtge.service';

interface TenantEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenant?: MtgeTenantAssignment;
  onSave: (data: UpdateTenantAssignmentData) => void | Promise<void>;
}

const TenantEditModal = ({ isOpen, onClose, tenant, onSave }: TenantEditModalProps) => {
  const [internetBreakout, setInternetBreakout] = useState(true);
  const [tunnelMode, setTunnelMode] = useState<'split' | 'full'>('split');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && tenant) {
      setInternetBreakout(tenant.internet_breakout === 1);
      setTunnelMode(tenant.tunnel_mode || 'split');
    }
  }, [isOpen, tenant]);

  const handleSave = async () => {
    if (!tenant) return;
    try {
      setSaving(true);
      await onSave({ internetBreakout, tunnelMode });
    } catch {
      // Error handled by parent
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <div className="flex items-center justify-end space-x-3">
      <button onClick={onClose} className="btn-secondary">Cancel</button>
      <button
        onClick={handleSave}
        disabled={saving}
        className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
      >
        <Settings className="w-4 h-4" />
        <span>{saving ? 'Saving...' : 'Save Changes'}</span>
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Tenant Settings: ${tenant?.tenant_name || tenant?.tenant_id?.substring(0, 8) || ''}`}
      size="md"
      footer={footer}
    >
      <div className="space-y-5">
        {/* Protocol (read-only) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">App VPN Protocol</label>
          <div className="flex items-center space-x-2">
            <span className={`px-3 py-1.5 text-sm font-medium rounded-full ${
              tenant?.tunnel_protocol === 'ikev2'
                ? 'bg-purple-100 text-purple-700'
                : 'bg-blue-100 text-blue-700'
            }`}>
              {tenant?.tunnel_protocol === 'ikev2' ? 'IKEv2' : 'WireGuard'}
            </span>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Changing protocol requires removing and re-assigning the tenant.
          </p>
        </div>

        {/* Internet Breakout */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Internet Breakout</label>
          <label className="flex items-start space-x-3 cursor-pointer">
            <div className="relative mt-0.5">
              <input
                type="checkbox"
                checked={internetBreakout}
                onChange={(e) => setInternetBreakout(e.target.checked)}
                className="sr-only"
              />
              <div className={`block w-10 h-6 rounded-full transition-colors ${
                internetBreakout ? 'bg-primary-600' : 'bg-gray-300'
              }`} />
              <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${
                internetBreakout ? 'translate-x-4' : ''
              }`} />
            </div>
            <div>
              <span className="block text-sm text-gray-700">
                {internetBreakout ? 'Enabled' : 'Disabled'}
              </span>
              <span className="block text-xs text-gray-500">
                Allow tenant traffic to reach the internet via shared WAN (SNAT).
              </span>
            </div>
          </label>
        </div>

        {/* Tunnel Mode */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Tunnel Mode</label>
          <div className="space-y-2">
            <label className={`flex items-start space-x-3 cursor-pointer p-3 rounded-lg border transition-colors hover:bg-gray-50 ${
              tunnelMode === 'split' ? 'border-primary-500 bg-primary-50' : 'border-gray-200'
            }`}>
              <input
                type="radio"
                name="tunnelMode"
                value="split"
                checked={tunnelMode === 'split'}
                onChange={() => setTunnelMode('split')}
                className="mt-0.5 h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <div>
                <span className="block text-sm font-medium text-gray-700">Split Tunnel</span>
                <span className="block text-xs text-gray-500">
                  Only VPN-destined traffic routes through the tunnel. Internet traffic uses the client's local connection.
                </span>
              </div>
            </label>
            <label className={`flex items-start space-x-3 cursor-pointer p-3 rounded-lg border transition-colors hover:bg-gray-50 ${
              tunnelMode === 'full' ? 'border-primary-500 bg-primary-50' : 'border-gray-200'
            }`}>
              <input
                type="radio"
                name="tunnelMode"
                value="full"
                checked={tunnelMode === 'full'}
                onChange={() => setTunnelMode('full')}
                className="mt-0.5 h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <div>
                <span className="block text-sm font-medium text-gray-700">Full Tunnel</span>
                <span className="block text-xs text-gray-500">
                  All client traffic routes through the tunnel. Requires internet breakout for web access.
                </span>
              </div>
            </label>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TenantEditModal;
